import React, { useState, useRef, useEffect } from 'react'
import { IS_BROWSER, enablePdpRedesign, i18nTranslate } from 'src/utils'
import { FormIcons } from 'src/views/components/Icons/FormIcons'
import './styles.scss'
import arrowIcon from 'src/images/arrow_icon.svg'
import { imagePlaceholder } from 'src/static/fixtures/imagePlaceholder'

const getImageNameFromUrl = url => {
  try {
    const pathname = new URL(url).pathname
    const filename = pathname
      .substring(pathname.lastIndexOf('/') + 1)
      .split('.')[0]
    return filename
      .replace(/[-_]+/g, ' ')
      .replace(/\b\w/g, char => char.toUpperCase())
  } catch {
    return 'Product Media'
  }
}

const productNormalizeMediaList = list => {
  if (!Array.isArray(list)) return []

  return list
    .map(item => {
      if (typeof item === 'string') {
        return { url: item, alt: getImageNameFromUrl(item) }
      } else if (typeof item === 'object' && item !== null) {
        const url = item.url || item.source || item.mediaUrl
        if (!url) return null
        return {
          url,
          thumbnailUrl:
            item.thumbnailUrl ||
            item.thumbnail ||
            item.thumb ||
            url ||
            undefined,
          alt: item.alt?.trim() || getImageNameFromUrl(url),
        }
      }
      return null
    })
    .filter(Boolean)
}

const getMediaType = url => {
  if (typeof url !== 'string') return 'image'
  if (url?.includes('youtube.com') || url?.includes('youtu.be'))
    return 'youtube'
  const extension = url?.split('.')?.pop()?.toLowerCase()
  return ['mp4', 'webm', 'ogg']?.includes(extension) ? 'video' : 'image'
}

const getYouTubeEmbedUrl = url => {
  try {
    const videoId = url?.includes('youtu.be')
      ? url?.split('youtu.be/')[1]
      : url?.split('v=')[1]?.split('&')[0]
    return `https://www.youtube.com/embed/${videoId}`
  } catch {
    return ''
  }
}

const MainMedia = ({ item }) => {
  const type = getMediaType(item?.url)
  if (type === 'youtube') {
    return (
      <iframe
        src={getYouTubeEmbedUrl(item?.url)}
        title={item?.alt}
        className="main-image"
        width="100%"
        height="100%"
        frameBorder="0"
        allowFullScreen
      />
    )
  }
  if (type === 'video') {
    return (
      <video
        src={item?.url}
        alt={item?.alt}
        className="main-image"
        controls
        width="100%"
        height="100%"
      />
    )
  }
  return (
    <img
      src={item?.url}
      alt={item?.alt}
      className="main-image"
      tabIndex={0}
      data-testid="qa-product-main-image"
      aria-label={item?.alt || 'Product Image'}
      width="100%"
      height="100%"
    />
  )
}

const ProductImageGallery = props => {
  const { productImageList = [], mainImageOverride = '' } = props
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [isExpanded, setIsExpanded] = useState(!enablePdpRedesign())
  const [windowWidth, setWindowWidth] = useState(
    IS_BROWSER ? window.innerWidth : 1200
  )
  const [showModal, setShowModal] = useState(false)
  const modalRef = useRef(null)
  const lastFocusedRef = useRef(null)
  const normalizedMediaList = productNormalizeMediaList(productImageList) || []
  const productList =
    normalizedMediaList.length > 0
      ? normalizedMediaList
      : [
          {
            url: imagePlaceholder,
            thumbnailUrl: imagePlaceholder,
            alt: 'Product image not available',
            isFallback: true,
          },
        ]

  const getMainDisplayItem = (index = 0) => {
    const baseItem = productList[index] || { url: '', alt: '' }
    if (index === 0 && mainImageOverride) {
      return {
        ...baseItem,
        url: mainImageOverride,
      }
    }
    return baseItem
  }

  useEffect(() => {
    if (!IS_BROWSER) return
    const handleResize = () => setWindowWidth(window.innerWidth)
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const isMobile = windowWidth < 1200

  const slidesRef = useRef(null)
  const videoRefs = useRef({})
  const autoScrollRef = useRef({ active: false, rafId: null })
  const touchGestureRef = useRef({ active: false })
  const scrollDebounceRef = useRef(null)

  const animateToSlide = (index, duration = 480) => {
    if (!slidesRef.current) return
    const slide = slidesRef.current.children[index]
    if (!slide) return

    const container = slidesRef.current
    const startLeft = container.scrollLeft
    const targetLeft = slide.offsetLeft

    if (Math.abs(targetLeft - startLeft) < 1) return

    if (autoScrollRef.current.rafId) {
      cancelAnimationFrame(autoScrollRef.current.rafId)
      autoScrollRef.current.rafId = null
    }

    autoScrollRef.current.active = true
    let startTime = null

    const step = timestamp => {
      if (!slidesRef.current) {
        autoScrollRef.current.active = false
        autoScrollRef.current.rafId = null
        return
      }

      if (!startTime) startTime = timestamp
      const elapsed = timestamp - startTime
      const progress = Math.min(elapsed / duration, 1)
      const eased = 1 - Math.pow(1 - progress, 3)
      const nextLeft = startLeft + (targetLeft - startLeft) * eased
      container.scrollTo({ left: nextLeft, behavior: 'auto' })

      if (progress < 1) {
        autoScrollRef.current.rafId = requestAnimationFrame(step)
      } else {
        autoScrollRef.current.active = false
        autoScrollRef.current.rafId = null
      }
    }

    autoScrollRef.current.rafId = requestAnimationFrame(step)
  }

  useEffect(() => {
    return () => {
      if (autoScrollRef.current.rafId) {
        cancelAnimationFrame(autoScrollRef.current.rafId)
      }
      if (scrollDebounceRef.current) {
        clearTimeout(scrollDebounceRef.current)
      }
    }
  }, [])

  useEffect(() => {
    if (!productList || productList.length === 0) return
    const item = productList[selectedIndex]
    if (!item) return
    const type = getMediaType(item?.url)

    // Pause all other videos
    Object.keys(videoRefs.current).forEach(k => {
      const idx = Number(k)
      const videoElement = videoRefs.current[k]
      if (videoElement && idx !== selectedIndex) {
        try {
          videoElement.pause()
        } catch (err) {}
      }
    })

    if (type === 'video') {
      const selectedVideo = videoRefs.current[selectedIndex]
      if (!selectedVideo) return
      const tryPlay = async () => {
        try {
          await selectedVideo.play()
        } catch (err) {
          // try muted play as a fallback for autoplay-restricted browsers
          try {
            selectedVideo.muted = true
            await selectedVideo.play()
            // try to unmute shortly after play starts
            setTimeout(() => {
              try {
                selectedVideo.muted = false
              } catch (e) {}
            }, 500)
          } catch (err2) {
            console.error(
              'Muted autoplay fallback failed for ProductImageGallery:',
              err2
            )
          }
        }
      }
      tryPlay()
    }
  }, [selectedIndex, productList])

  useEffect(() => {
    if (enablePdpRedesign() && isMobile && slidesRef.current) {
      // Skip programmatic scroll during an active touch gesture —
      // CSS scroll-snap already handles the visual position
      if (touchGestureRef.current.active) return
      const slide = slidesRef.current.children[selectedIndex]
      if (slide) {
        slide.scrollIntoView({
          behavior: 'auto',
          inline: 'center',
          block: 'nearest',
        })
      }
    }
  }, [selectedIndex, isMobile])

  // Modal keyboard navigation (always call hooks at top level)
  useEffect(() => {
    if (!(enablePdpRedesign() && !isMobile && showModal)) return
    const handleKeyDown = e => {
      if (e.key === 'ArrowLeft') {
        setSelectedIndex(
          i => (i - 1 + productList?.length) % productList?.length
        )
      }
      if (e.key === 'ArrowRight') {
        setSelectedIndex(i => (i + 1) % productList?.length)
      }
      if (e.key === 'Escape') {
        setShowModal(false)
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [showModal, productList?.length, isMobile])

  // When modal opens, focus the modal container and trap Tab inside it
  useEffect(() => {
    if (!showModal || !modalRef.current) return

    // prefer focusing the first interactive control inside the modal (previous arrow)
    const node = modalRef.current
    const focusTimer = setTimeout(() => {
      try {
        if (!node) return
        // button has the class on itself, so match button.redesign-gallery-modal-arrow.left
        const prefer =
          node.querySelector('button.redesign-gallery-modal-arrow.left') ||
          node.querySelector('button, [tabindex]:not([tabindex="-1"])')
        if (prefer && typeof prefer.focus === 'function') {
          prefer.focus()
        } else if (node && typeof node.focus === 'function') {
          node.focus()
        }
      } catch (err) {
        /* no-op */
      }
    }, 0)

    const handleModalKey = e => {
      if (e.key !== 'Tab') return
      const focusable = node.querySelectorAll(
        'a[href], area[href], input:not([disabled]):not([type=hidden]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), iframe, [tabindex]:not([tabindex="-1"]), [contenteditable]'
      )
      const focusableElems = Array.prototype.slice
        .call(focusable)
        .filter(
          el => el.offsetWidth || el.offsetHeight || el.getClientRects().length
        )
      if (focusableElems.length === 0) {
        e.preventDefault()
        return
      }
      const first = focusableElems[0]
      const last = focusableElems[focusableElems.length - 1]
      const active = document.activeElement
      if (!e.shiftKey && active === last) {
        e.preventDefault()
        first.focus()
      } else if (e.shiftKey && active === first) {
        e.preventDefault()
        last.focus()
      }
    }

    node.addEventListener('keydown', handleModalKey)
    return () => {
      clearTimeout(focusTimer)
      node.removeEventListener('keydown', handleModalKey)
    }
  }, [showModal])

  useEffect(() => {
    const productWrapEle = document.getElementsByClassName('product-wrap')[0]
    if (!productWrapEle) return
    productWrapEle.style.zIndex = showModal ? '999' : '99'

    const originalOverflow = document.body.style.overflow
    const originalPaddingRight = document.body.style.paddingRight

    if (showModal) {
      const scrollBarWidth =
        window.innerWidth - document.documentElement.clientWidth
      if (scrollBarWidth > 0) {
        document.body.style.paddingRight = `${scrollBarWidth}px`
      }
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = originalOverflow || ''
      document.body.style.paddingRight = originalPaddingRight || ''
    }

    return () => {
      productWrapEle.style.zIndex = '99'
      document.body.style.overflow = originalOverflow || ''
      document.body.style.paddingRight = originalPaddingRight || ''
    }
  }, [showModal])

  const handleTouchStart = () => {
    touchGestureRef.current.active = true
    if (scrollDebounceRef.current) {
      clearTimeout(scrollDebounceRef.current)
    }
  }

  const handleTouchEnd = () => {
    // Clear gesture state — do NOT update index here.
    // Let handleScroll with its 300ms debounce handle all index updates naturally
    // so momentum scroll can settle before final index calculation
    touchGestureRef.current.active = false
  }

  const handleScroll = () => {
    if (!slidesRef.current) return
    if (autoScrollRef.current.active) return

    if (scrollDebounceRef.current) {
      clearTimeout(scrollDebounceRef.current)
    }

    // Debounce so we only update after momentum scroll settles
    scrollDebounceRef.current = setTimeout(() => {
      if (!slidesRef.current) return
      const container = slidesRef.current
      const children = Array.from(container.children)
      if (!children.length) return
      const slideWidth = children[0]?.offsetWidth || 1
      const nearestIndex = Math.round(container.scrollLeft / slideWidth)
      const clamped = Math.max(
        0,
        Math.min((productList?.length || 1) - 1, nearestIndex)
      )
      setSelectedIndex(clamped)
    }, 50)
  }

  // Handle tap/click on image to go to next slide (mobile redesign only)
  const handleImageClick = () => {
    if (productList?.length > 1) {
      setSelectedIndex((selectedIndex + 1) % productList?.length)
    }
  }

  // Toggle play/pause for a video at index; if playing, pause and advance to next slide.
  const handleVideoToggle = idx => {
    const videoElement = videoRefs.current[idx]
    try {
      if (videoElement) {
        if (videoElement.paused) {
          videoElement.play().catch(() => {})
          return
        }
        // if playing, pause and advance immediately
        videoElement.pause()
      }
    } catch (err) {}
    setSelectedIndex(i => (i + 1) % productList?.length)
  }

  // Redesign mobile carousel (with swipe/scroll support)
  if (enablePdpRedesign() && isMobile) {
    return (
      <div className="redesign-gallery-mobile">
        <div className="redesign-gallery-mobile-carousel">
          <div
            className="redesign-gallery-mobile-slides"
            ref={slidesRef}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            onTouchCancel={handleTouchEnd}
            onScroll={handleScroll}>
            {productList?.map((item, index) => {
              const displayItem = getMainDisplayItem(index)
              const mediaUrl = displayItem?.url
              const thumbnailUrl = item?.thumbnailUrl || mediaUrl
              const type = getMediaType(mediaUrl)
              return (
                <div
                  className="redesign-gallery-mobile-slide"
                  key={`slide-${index}`}
                  aria-roledescription="slide"
                  aria-label={`${index + 1} of ${productList?.length}`}>
                  {type === 'youtube' ? (
                    <iframe
                      className="redesign-gallery-mobile-media"
                      src={getYouTubeEmbedUrl(thumbnailUrl)}
                      title={item?.alt || 'Product Video'}
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  ) : type === 'video' ? (
                    <div style={{ position: 'relative' }}>
                      <video
                        ref={el => (videoRefs.current[index] = el)}
                        className="redesign-gallery-mobile-media"
                        controls
                        playsInline
                        onClick={e => {
                          e.stopPropagation()
                          handleVideoToggle(index)
                        }}
                        onTouchStart={e => e.stopPropagation()}
                        onEnded={() => {
                          setSelectedIndex(i => (i + 1) % productList?.length)
                        }}>
                        <source
                          src={mediaUrl}
                          type={`video/${mediaUrl?.split('.')?.pop()}`}
                        />
                      </video>
                      <button
                        type="button"
                        aria-label="Advance slide"
                        className="redesign-gallery-mobile-video-overlay"
                        onClick={e => {
                          e.stopPropagation()
                          handleVideoToggle(index)
                        }}
                      />
                    </div>
                  ) : (
                    <img
                      className="redesign-gallery-mobile-media"
                      src={mediaUrl}
                      alt={item?.alt}
                    />
                  )}
                </div>
              )
            })}
          </div>
          {productList?.length > 1 && (
            <div
              className="redesign-gallery-mobile-dots"
              role="tablist"
              aria-label="Carousel indicators">
              {productList?.map((_, i) => (
                <button
                  key={`dot-${i}`}
                  role="tab"
                  aria-selected={i === selectedIndex}
                  aria-label={`Go to slide ${i + 1} of ${productList?.length}`}
                  className={`redesign-gallery-mobile-dot${i === selectedIndex ? ' active' : ''}`}
                  onClick={() => {
                    if (slidesRef.current) {
                      const slide = slidesRef.current.children[i]
                      if (slide) {
                        slide.scrollIntoView({
                          behavior: 'auto',
                          inline: 'center',
                          block: 'nearest',
                        })
                      }
                    }
                    setSelectedIndex(i)
                  }}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    )
  }

  const handleOpenModal = idx => {
    try {
      lastFocusedRef.current = document.activeElement
    } catch (err) {
      lastFocusedRef.current = null
    }
    setSelectedIndex(idx)
    setShowModal(true)
  }

  const handleCloseModal = () => {
    setShowModal(false)
    try {
      if (
        lastFocusedRef.current &&
        typeof lastFocusedRef.current.focus === 'function'
      ) {
        lastFocusedRef.current.focus()
      }
    } catch (err) {
      /* no-op */
    }
  }

  const handlePrev = () =>
    setSelectedIndex(i => (i - 1 + productList?.length) % productList?.length)
  const handleNext = () => setSelectedIndex(i => (i + 1) % productList?.length)

  return (
    <>
      <div className="redesign-gallery">
        <div
          className="redesign-gallery-main-image-wrapper"
          onClick={() => {
            if (enablePdpRedesign()) {
              handleOpenModal(selectedIndex)
            } else {
              setSelectedIndex(selectedIndex)
            }
          }}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              if (enablePdpRedesign()) {
                handleOpenModal(selectedIndex)
              } else {
                setSelectedIndex(selectedIndex)
              }
            }
          }}
          style={{ cursor: 'pointer' }}>
          <MainMedia item={getMainDisplayItem(selectedIndex)} />
        </div>
        {productList?.length > 1 && (
          <div className="redesign-gallery-thumbnail-list">
            {!isExpanded
              ? (() => {
                  // Show 3 thumbnails, then grey box if more than 4 images
                  const thumbnails = []
                  const showGreyBox = productList?.length > 4
                  const maxThumbs = showGreyBox
                    ? 3
                    : Math.min(4, productList?.length)
                  for (let i = 0; i < maxThumbs; i++) {
                    const item = productList[i]
                    const type = getMediaType(item?.url)
                    thumbnails?.push(
                      <div
                        key={`thumb-${i}`}
                        className="redesign-gallery-thumbnail-wrapper"
                        onClick={() => handleOpenModal(i)}
                        onKeyDown={e => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            handleOpenModal(i)
                          }
                        }}
                        tabIndex={0}
                        role="button"
                        aria-label={`View ${item?.alt}, click to select`}
                        aria-pressed={false}
                        style={{ cursor: 'pointer' }}>
                        {type === 'youtube' ? (
                          <img
                            src={`https://img.youtube.com/vi/${getYouTubeEmbedUrl(item?.url)?.split('/embed/')[1]}/mqdefault.jpg`}
                            alt={item?.alt}
                            className="redesign-gallery-image-thumbnail"
                          />
                        ) : type === 'video' ? (
                          <div className="redesign-gallery-video-thumbnail">
                            <video muted>
                              <source
                                src={item?.url}
                                type={`video/${item?.url.split('.').pop()}`}
                              />
                            </video>
                            <span className="redesign-gallery-play-icon">
                              <FormIcons
                                icontype="play-arrow"
                                width="14px"
                                color="white"
                                ariaLabel={i18nTranslate(
                                  'gallery.playVideo',
                                  'Play video',
                                  { nameSpace: 'ssr-resource' }
                                )}
                              />
                            </span>
                          </div>
                        ) : (
                          <img
                            src={item?.url}
                            alt={item?.alt}
                            className="redesign-gallery-image-thumbnail"
                          />
                        )}
                      </div>
                    )
                  }
                  if (showGreyBox) {
                    thumbnails?.push(
                      <button
                        type="button"
                        className="redesign-gallery-thumbnail-wrapper redesign-gallery-more-tile"
                        aria-label={`View ${productList?.length - 3} more images`}
                        onClick={() => setIsExpanded(true)}
                        key="thumb-grey">
                        <span className="redesign-gallery-more-count">
                          +{productList?.length - 3}
                        </span>
                      </button>
                    )
                  }
                  return thumbnails
                })()
              : productList?.map((item, index) => {
                  const type = getMediaType(item?.url)
                  return (
                    <div
                      key={`thumb-${index}`}
                      className="redesign-gallery-thumbnail-wrapper"
                      onClick={() => {
                        if (enablePdpRedesign()) {
                          handleOpenModal(index)
                        } else {
                          setSelectedIndex(index)
                        }
                      }}
                      onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          if (enablePdpRedesign()) {
                            handleOpenModal(index)
                          } else {
                            setSelectedIndex(index)
                          }
                        }
                      }}
                      tabIndex={0}
                      role="button"
                      aria-label={`View ${item?.alt}, click to select`}
                      aria-pressed={false}
                      style={{ cursor: 'pointer' }}>
                      {type === 'youtube' ? (
                        <img
                          src={`https://img.youtube.com/vi/${getYouTubeEmbedUrl(item?.url)?.split('/embed/')[1]}/mqdefault.jpg`}
                          alt={item?.alt}
                          className="redesign-gallery-image-thumbnail"
                        />
                      ) : type === 'video' ? (
                        <div className="redesign-gallery-video-thumbnail">
                          <video muted>
                            <source
                              src={item?.url}
                              type={`video/${item?.url?.split('.')?.pop()}`}
                            />
                          </video>
                          <span className="redesign-gallery-play-icon">
                            <FormIcons
                              icontype="play-arrow"
                              width="14px"
                              color="white"
                              ariaLabel={i18nTranslate(
                                'gallery.playVideo',
                                'Play video',
                                { nameSpace: 'ssr-resource' }
                              )}
                            />
                          </span>
                        </div>
                      ) : (
                        <img
                          src={item?.url}
                          alt={item?.alt}
                          className="redesign-gallery-image-thumbnail"
                        />
                      )}
                    </div>
                  )
                })}
          </div>
        )}
      </div>
      {showModal && (
        <div className="redesign-gallery-modal-overlay">
          <div
            className="redesign-gallery-modal"
            ref={modalRef}
            tabIndex={-1}
            role="dialog"
            aria-modal="true"
            aria-label="Image gallery modal">
            <div className="redesign-gallery-modal-main">
              <div className="redesign-gallery-modal-image">
                <MainMedia item={getMainDisplayItem(selectedIndex)} />
              </div>
              <div className="redesign-gallery-modal-arrow">
                <button
                  className="redesign-gallery-modal-arrow left"
                  aria-label="Previous image"
                  onClick={handlePrev}>
                  <img
                    src={arrowIcon}
                    alt="Previous"
                    className="redesign-gallery-modal-arrow-icon left"
                  />
                </button>
                <div tabIndex={0} className="redesign-gallery-modal-count">
                  {selectedIndex + 1} of {productList?.length}
                </div>
                <button
                  className="redesign-gallery-modal-arrow right"
                  aria-label="Next image"
                  onClick={handleNext}>
                  <img
                    src={arrowIcon}
                    alt="Next"
                    className="redesign-gallery-modal-arrow-icon right"
                  />
                </button>
              </div>
            </div>
            <div className="redesign-gallery-modal-thumbnails">
              {productList?.map((item, idx) => {
                const type = getMediaType(item?.url)
                return (
                  <div
                    key={`modal-thumb-${idx}`}
                    className={
                      idx === selectedIndex
                        ? 'redesign-gallery-modal-thumb active'
                        : 'redesign-gallery-modal-thumb'
                    }
                    onClick={() => setSelectedIndex(idx)}
                    tabIndex={0}
                    role="button"
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        setSelectedIndex(idx)
                      }
                    }}>
                    {type === 'youtube' ? (
                      <img
                        src={`https://img.youtube.com/vi/${getYouTubeEmbedUrl(item?.url)?.split('/embed/')[1]}/mqdefault.jpg`}
                        alt={item?.alt}
                        className="redesign-gallery-modal-thumb-image"
                      />
                    ) : type === 'video' ? (
                      <div className="redesign-gallery-modal-video-thumbnail">
                        <video muted>
                          <source
                            src={item?.url}
                            type={`video/${item?.url?.split('.')?.pop()}`}
                          />
                        </video>
                        <span className="redesign-gallery-play-icon">
                          <FormIcons
                            icontype="play-arrow"
                            width="12px"
                            color="white"
                            ariaLabel={i18nTranslate(
                              'gallery.playVideo',
                              'Play video',
                              { nameSpace: 'ssr-resource' }
                            )}
                          />
                        </span>
                      </div>
                    ) : (
                      <img
                        src={item?.thumbnailUrl || item?.url}
                        alt={item?.alt}
                        className="redesign-gallery-modal-thumb-image"
                      />
                    )}
                  </div>
                )
              })}
            </div>

            <button
              className="redesign-gallery-modal-close"
              aria-label="Close"
              onClick={handleCloseModal}>
              ×
            </button>
          </div>
        </div>
      )}
    </>
  )
}

export default ProductImageGallery
